// src/components/MemberDashboard.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaUser, FaCalendarAlt, FaClock, FaMapMarkerAlt, FaVideo, FaEnvelope, FaBell, FaShoppingCart, FaGraduationCap, FaAward, FaUsers, FaTachometerAlt, FaStore, FaCertificate, FaSignOutAlt, FaBars, FaTimes, FaBuilding, FaGlobe, FaStar } from 'react-icons/fa';
import PublicLayout from './public/PublicLayout';
import MembershipCertificate from './membership/MembershipCertificate';

// ARCA Color Scheme
const ARCA_COLORS = {
  primary: '#6B8E23',
  secondary: '#58761C',
  darkGray: '#4A4F4F',
  lightGray: '#A0A5A5',
  success: '#28a745',
  white: '#FFFFFF',
  warning: '#ffc107',
  danger: '#dc3545',
  info: '#0d6efd'
};

const MemberDashboard = ({ user, onLogout }) => {
  const [dashboardData, setDashboardData] = useState({
    profile: null,
    registeredEvents: [],
    enrolledCourses: [],
    cartItems: [],
    notifications: [],
    purchases: [],
    certificates: [],
    messages: [],
    messageCount: 0,
    notificationCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [membershipPricing, setMembershipPricing] = useState({});
  const [userReceipts, setUserReceipts] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const isDesktop = () => window.innerWidth > 991;

  // Set sidebar state based on screen size, and close on mobile/tablet route changes
  useEffect(() => {
    const updateSidebar = () => {
      setSidebarOpen(isDesktop());
    };
    updateSidebar();
    window.addEventListener('resize', updateSidebar);
    return () => window.removeEventListener('resize', updateSidebar);
  }, []);

  useEffect(() => {
    if (!isDesktop()) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  // Membership types with prices (will be populated from backend)
  const membershipTypes = [
    {
      id: 'international',
      name: 'International Membership',
      duration: '1 Year',
      price: membershipPricing.international || 20,
      description: 'For professionals outside Africa',
      benefits: ['Global resources', 'International collaboration', 'Virtual events', 'Research partnerships']
    },
    {
      id: 'associate',
      name: 'Associate Membership',
      duration: '1 Year',
      price: membershipPricing.associate || 15,
      description: 'For allied health professionals',
      benefits: ['Basic resource access', 'Selected webinars', 'Networking opportunities', 'Event notifications']
    },
    {
      id: 'professional',
      name: 'Professional Membership',
      duration: '1 Year',
      price: membershipPricing.professional || 10,
      description: 'For qualified respiratory care professionals',
      benefits: ['Full access to all resources', 'CPD tracking', 'Conference discounts', 'Networking opportunities']
    },
    {
      id: 'student',
      name: 'Student Membership',
      duration: '1 Year',
      price: membershipPricing.student || 5,
      description: 'For students in respiratory care programs',
      benefits: ['Educational resources', 'Student webinars', 'Mentorship programs', 'Career guidance']
    }
  ];

  useEffect(() => {
    fetchMembershipPricing();
    fetchAllDashboardData();
  }, []);

  useEffect(() => {
    if (dashboardData.profile?.id) {
      fetchUserReceipts();
    }
  }, [dashboardData.profile]);

  const fetchMembershipPricing = async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL;

      if (!API_BASE) {
        console.error('❌ REACT_APP_API_URL not configured!');
        return;
      }
      const response = await fetch(`${API_BASE}/memberships/pricing`);
      const data = await response.json();
      if (data.success && data.data.pricing) {
        setMembershipPricing(data.data.pricing);
        console.log('✅ Membership pricing loaded:', data.data.pricing);
      }
    } catch (error) {
      console.error('⚠️ Error fetching membership pricing:', error);
      // Fallback to default prices if API fails
    }
  };

  const fetchAllDashboardData = async () => {
    try {
      const token = localStorage.getItem('authToken') ||
        localStorage.getItem('userToken') ||
        localStorage.getItem('directorToken') ||
        localStorage.getItem('adminToken');

      console.log('🔍 [MemberDashboard] Fetching dashboard data...');
      console.log('🔑 Token found:', token ? 'Yes' : 'No');

      if (!token) {
        console.warn('⚠️ No authentication token found!');
        setLoading(false);
        return;
      }

      // Fetch all dashboard data in parallel
      const API_BASE = process.env.REACT_APP_API_URL;

      if (!API_BASE) {
        console.error('❌ REACT_APP_API_URL not configured!');
        setLoading(false);
        return;
      }
      const [
        profileResponse,
        eventsResponse,
        coursesResponse,
        cartResponse,
        notificationsResponse,
        purchasesResponse,
        certificatesResponse,
        messagesResponse
      ] = await Promise.all([
        fetch(`${API_BASE}/users/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE}/events/my-events`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE}/university/enrollments`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE}/store/cart`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE}/users/notifications`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE}/store/orders`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        // Certificates endpoint might not exist, so we'll use a placeholder
        Promise.resolve({ ok: false }),
        // Messages endpoint
        fetch(`${API_BASE}/messaging/my-messages`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      console.log('📊 Profile API Response Status:', profileResponse.status);

      const profileData = profileResponse.ok ? (await profileResponse.json()).data.profile : null;

      if (profileData) {
        console.log('✅ Profile Data Loaded:', {
          name: `${profileData.first_name} ${profileData.last_name}`,
          email: profileData.email,
          member_number: profileData.member_number,
          profile_picture_url: profileData.profile_picture_url,
          membership_status: profileData.membership_status
        });
      } else {
        console.error('❌ Failed to load profile data. Response:', profileResponse.status);
        if (!profileResponse.ok) {
          const errorText = await profileResponse.text();
          console.error('Error details:', errorText);
        }
      }

      const eventsData = eventsResponse.ok ? (await eventsResponse.json()).data.registrations || [] : [];
      const coursesData = coursesResponse.ok ? (await coursesResponse.json()).data || [] : [];
      const cartData = cartResponse.ok ? (await cartResponse.json()).data.cart?.items || [] : [];
      const notificationsData = notificationsResponse.ok ? (await notificationsResponse.json()).data.notifications || [] : [];
      const purchasesData = purchasesResponse.ok ? (await purchasesResponse.json()).data.orders || [] : [];
      const certificatesData = certificatesResponse.ok ? (await certificatesResponse.json()).data || [] : [];

      // Process messages
      let messagesData = [];
      let messageCount = 0;
      let unreadCount = 0;
      if (messagesResponse.ok) {
        const messagesJson = await messagesResponse.json();
        messagesData = messagesJson.data?.messages || messagesJson.data || [];
        messageCount = messagesJson.data?.count || messagesData.length;
        unreadCount = messagesJson.data?.unreadCount || messagesData.filter(m => m.status !== 'read').length;
        console.log('📧 Messages loaded:', messageCount, '| Unread:', unreadCount);
      }

      setDashboardData({
        profile: profileData,
        registeredEvents: eventsData.slice(0, 3),
        enrolledCourses: coursesData.slice(0, 3),
        cartItems: cartData,
        notifications: notificationsData.slice(0, 3),
        purchases: purchasesData.slice(0, 3),
        certificates: certificatesData.slice(0, 3),
        messages: messagesData.slice(0, 3),
        messageCount: unreadCount,  // Show unread count
        notificationCount: notificationsData.length
      });

      console.log('✅ Dashboard data set successfully');
      setLoading(false);
    } catch (error) {
      console.error('❌ Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const fetchUserReceipts = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('userToken');
      const API_BASE = process.env.REACT_APP_API_URL;

      if (!API_BASE || !token || !dashboardData.profile?.id) return;

      const response = await fetch(`${API_BASE}/receipts/user/${dashboardData.profile.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUserReceipts(data.receipts || []);
        }
      }
    } catch (error) {
      console.error('Error fetching receipts:', error);
    }
  };

  const downloadReceipt = async (receiptNumber) => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) return;

      window.open(`${API_BASE}/receipts/download/${receiptNumber}`, '_blank');
    } catch (error) {
      console.error('Error downloading receipt:', error);
      alert('Failed to download receipt. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'America/New_York'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const est = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York' });
    const cst = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago' });
    const gmt = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });
    return `${est} EST / ${cst} CST / ${gmt} GMT`;
  };

  if (loading) {
    return (
      <PublicLayout user={user} onLogout={onLogout}>
        <div className="container py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-4 text-muted">Loading your dashboard...</p>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout user={user} onLogout={onLogout}>
      <style>{`
        /* Sidebar Styles */
        .dashboard-sidebar {
          position: fixed;
          left: 0;
          top: 0;
          width: 260px;
          height: 100vh;
          background: linear-gradient(180deg, ${ARCA_COLORS.primary} 0%, ${ARCA_COLORS.secondary} 100%);
          box-shadow: 2px 0 10px rgba(0,0,0,0.1);
          z-index: 1040;
          transition: transform 0.3s ease;
          display: flex;
          flex-direction: column;
        }

        .sidebar-nav-scroll {
          flex: 1;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
        }
        
        .dashboard-sidebar.closed {
          transform: translateX(-100%);
        }
        
        .sidebar-header {
          padding: 20px;
          background: rgba(0,0,0,0.1);
          border-bottom: 2px solid rgba(255,255,255,0.1);
        }
        
        .sidebar-logo {
          color: ${ARCA_COLORS.white};
          font-size: 24px;
          font-weight: bold;
          text-align: center;
          margin-bottom: 10px;
        }
        
        .sidebar-user {
          color: ${ARCA_COLORS.white};
          font-size: 14px;
          text-align: center;
        }
        
        .sidebar-nav {
          padding: 20px 0;
          min-height: min-content;
        }
        
        .sidebar-nav-item {
          display: flex;
          align-items: center;
          padding: 12px 20px;
          color: rgba(255,255,255,0.9);
          text-decoration: none;
          transition: all 0.3s ease;
          border-left: 4px solid transparent;
          position: relative;
          animation: slideIn 0.5s ease-out;
        }
        
        .sidebar-nav-item:nth-child(1) { animation-delay: 0.1s; }
        .sidebar-nav-item:nth-child(2) { animation-delay: 0.15s; }
        .sidebar-nav-item:nth-child(3) { animation-delay: 0.2s; }
        .sidebar-nav-item:nth-child(4) { animation-delay: 0.25s; }
        .sidebar-nav-item:nth-child(5) { animation-delay: 0.3s; }
        .sidebar-nav-item:nth-child(6) { animation-delay: 0.35s; }
        .sidebar-nav-item:nth-child(7) { animation-delay: 0.4s; }
        .sidebar-nav-item:nth-child(8) { animation-delay: 0.45s; }
        .sidebar-nav-item:nth-child(9) { animation-delay: 0.5s; }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .sidebar-nav-item:hover {
          background: rgba(255,255,255,0.1);
          border-left-color: ${ARCA_COLORS.white};
          padding-left: 25px;
        }
        
        .sidebar-nav-item.active {
          background: rgba(255,255,255,0.15);
          border-left-color: ${ARCA_COLORS.white};
          font-weight: 600;
        }
        
        .sidebar-nav-icon {
          margin-right: 12px;
          font-size: 18px;
        }
        
        .sidebar-badge {
          margin-left: auto;
          background: ${ARCA_COLORS.danger};
          color: white;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: bold;
          animation: pulse 2s infinite;
        }
        
        .sidebar-logout {
          padding: 12px 20px;
          margin: 10px 20px;
          background: ${ARCA_COLORS.danger};
          color: white;
          border: none;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }
        
        .sidebar-logout:hover {
          background: #c82333;
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.2);
        }
        
        .sidebar-toggle {
          display: none;
          position: fixed;
          left: 15px;
          top: 128px;
          z-index: 1050;
          background: #ffffff;
          color: ${ARCA_COLORS.primary};
          border: 2px solid ${ARCA_COLORS.primary};
          padding: 10px 16px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 3px 10px rgba(0,0,0,0.25);
          font-weight: 600;
          font-size: 14px;
        }

        .sidebar-toggle:hover {
          background: ${ARCA_COLORS.primary};
          color: #ffffff;
          transform: scale(1.05);
        }

        @media (max-width: 991px) {
          .sidebar-toggle {
            display: flex;
            align-items: center;
            gap: 6px;
          }
        }
        
        .dashboard-content {
          margin-left: 260px;
          transition: margin-left 0.3s ease;
          min-height: 100vh;
        }
        
        .dashboard-content.full-width {
          margin-left: 0;
        }

        .sidebar-overlay {
          display: none;
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .stat-card {
          animation: fadeInUp 0.5s ease-out;
          transition: all 0.3s ease;
        }
        
        .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 16px rgba(0,0,0,0.1) !important;
        }
        
        .stat-card:nth-child(1) { animation-delay: 0.1s; }
        .stat-card:nth-child(2) { animation-delay: 0.2s; }
        .stat-card:nth-child(3) { animation-delay: 0.3s; }
        .stat-card:nth-child(4) { animation-delay: 0.4s; }
        .stat-card:nth-child(5) { animation-delay: 0.5s; }
        .stat-card:nth-child(6) { animation-delay: 0.6s; }
        .stat-card:nth-child(7) { animation-delay: 0.7s; }
        
        .pulse-badge {
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }
        
        @media (max-width: 991px) {
          .dashboard-sidebar {
            transform: translateX(-100%);
            width: 280px;
            max-width: 85vw;
            top: 116px;
            bottom: 0;
            height: auto;
            z-index: 1015;
          }
          .dashboard-sidebar.open {
            transform: translateX(0);
            box-shadow: 4px 0 20px rgba(0,0,0,0.3);
          }
          .dashboard-content {
            margin-left: 0 !important;
          }
          .sidebar-overlay {
            z-index: 1014;
            top: 116px;
            bottom: 0;
            height: auto;
          }
          .sidebar-overlay.open {
            display: block;
          }
        }
      `}</style>

      {/* Sidebar Toggle Button */}
      <button
        className="sidebar-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <FaTimes /> : <FaBars />}
        <span className="d-none d-sm-inline">Menu</span>
      </button>

      {/* Mobile overlay */}
      <div
        className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
        onClick={() => setSidebarOpen(false)}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1035
        }}
      />

      {/* Sidebar */}
      <div
        className={`dashboard-sidebar ${!sidebarOpen ? 'closed' : 'open'}`}
        style={{
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)'
        }}
      >
        <div className="sidebar-header">
          <div className="d-flex justify-content-between align-items-start">
            <div className="sidebar-logo">ARCA</div>
            <button
              className="btn btn-sm btn-link text-white d-md-none p-0"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
              style={{ fontSize: '1.2rem', lineHeight: 1 }}
            >
              <FaTimes />
            </button>
          </div>
          <div className="sidebar-user">
            {dashboardData.profile?.first_name || user?.firstName || 'Member'}
            {dashboardData.profile?.member_number && (
              <div className="small mt-1">{dashboardData.profile.member_number}</div>
            )}
          </div>
        </div>

        <div className="sidebar-nav-scroll">
          <div className="sidebar-nav">
            <Link
              to="/dashboard"
              className={`sidebar-nav-item ${location.pathname === '/dashboard' ? 'active' : ''}`}
            >
              <FaTachometerAlt className="sidebar-nav-icon" />
              Dashboard
            </Link>

            <Link
              to="/profile"
              className={`sidebar-nav-item ${location.pathname === '/profile' ? 'active' : ''}`}
            >
              <FaUser className="sidebar-nav-icon" />
              Profile
            </Link>

            <Link
              to="/messages"
              className={`sidebar-nav-item ${location.pathname === '/messages' ? 'active' : ''}`}
            >
              <FaEnvelope className="sidebar-nav-icon" />
              Messages
              {dashboardData.messageCount > 0 && (
                <span className="sidebar-badge">{dashboardData.messageCount}</span>
              )}
            </Link>

            <Link
              to="/notifications"
              className={`sidebar-nav-item ${location.pathname === '/notifications' ? 'active' : ''}`}
            >
              <FaBell className="sidebar-nav-icon" />
              Notifications
              {dashboardData.notificationCount > 0 && (
                <span className="sidebar-badge">{dashboardData.notificationCount}</span>
              )}
            </Link>

            <Link
              to="/events"
              className={`sidebar-nav-item ${location.pathname === '/events' ? 'active' : ''}`}
            >
              <FaCalendarAlt className="sidebar-nav-icon" />
              Events
            </Link>

            <Link
              to="/courses"
              className={`sidebar-nav-item ${location.pathname === '/courses' ? 'active' : ''}`}
            >
              <FaGraduationCap className="sidebar-nav-icon" />
              Courses
            </Link>

            <Link
              to="/community/announcements"
              className={`sidebar-nav-item ${location.pathname.startsWith('/community') ? 'active' : ''}`}
            >
              <FaUsers className="sidebar-nav-icon" />
              Community
            </Link>

            <Link
              to="/store"
              className={`sidebar-nav-item ${location.pathname === '/store' ? 'active' : ''}`}
            >
              <FaStore className="sidebar-nav-icon" />
              Store
            </Link>

            <Link
              to="/purchases"
              className={`sidebar-nav-item ${location.pathname === '/purchases' ? 'active' : ''}`}
            >
              <FaShoppingCart className="sidebar-nav-icon" />
              My Purchases
            </Link>

            <Link
              to="/certificates"
              className={`sidebar-nav-item ${location.pathname === '/certificates' ? 'active' : ''}`}
            >
              <FaCertificate className="sidebar-nav-icon" />
              Certificates
            </Link>

            <button className="sidebar-logout" onClick={onLogout}>
              <FaSignOutAlt style={{ marginRight: '8px' }} />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`dashboard-content ${!sidebarOpen ? 'full-width' : ''}`}>
        <div className="bg-light py-4">
          <div className="container">
            {/* Dashboard Header */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
              <div className="text-center text-md-start">
                <h1 className="h3 fw-bold text-dark mb-2">
                  {dashboardData.profile?.membership_type === 'vendor' ? 'Vendor Dashboard' : 'Member Dashboard'}
                </h1>
                <p className="text-muted mb-0">
                  {dashboardData.profile?.membership_type === 'vendor'
                    ? `Welcome back! Managing your ARCA Vendor Partnership.`
                    : "Welcome back! Here's what's happening with your ARCA membership."}
                </p>
              </div>
              <div className="d-flex align-items-center gap-3 justify-content-center">
                {/* Profile Picture */}
                {dashboardData.profile?.profile_picture_url ? (
                  <img
                    src={dashboardData.profile.profile_picture_url.startsWith('http')
                      ? dashboardData.profile.profile_picture_url
                      : `${process.env.REACT_APP_BACKEND_URL || 'https://api.africaresp.org'}${dashboardData.profile.profile_picture_url}`}
                    alt="Profile"
                    className="rounded-circle"
                    style={{ width: '40px', height: '40px', objectFit: 'cover', border: '2px solid #dee2e6' }}
                  />
                ) : (
                  <div className="bg-secondary rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                    <FaUser className="text-white" size={20} />
                  </div>
                )}
                <div className="text-center text-md-start">
                  <div className="fw-medium text-dark">
                    {dashboardData.profile?.first_name || user?.firstName || user?.email}
                  </div>
                  {dashboardData.profile?.member_number && (
                    <small className="text-muted d-block">{dashboardData.profile.member_number}</small>
                  )}
                </div>
                {/* Only Profile button - Logout is in sidebar */}
                <Link to="/profile" className="btn btn-sm" style={{ backgroundColor: ARCA_COLORS.primary, color: 'white' }}>
                  <FaUser className="me-1" />
                  <span className="d-none d-sm-inline">Profile</span>
                </Link>
              </div>
            </div>

            {/* Quick Stats Summary - Removed duplicate navigation items */}
            {/* Stats cards removed to reduce redundancy with sidebar navigation */}

            {/* Membership Status Button */}
            <div className="row g-3 g-md-4 mb-4">
              <div className="col-12">
                {dashboardData.profile?.membership_status === 'active' ? (
                  <div className="card border-0 shadow-sm">
                    <div className="card-body text-center p-4">
                      <button
                        className="btn btn-lg w-100 fw-bold text-white mb-3"
                        style={{ backgroundColor: '#28a745', border: 'none', cursor: 'default', pointerEvents: 'none' }}
                      >
                        <i className="fas fa-check-circle me-2"></i>
                        Membership Active – In Good Standing
                      </button>
                      <p className="text-muted small mb-0">
                        <i className="fas fa-info-circle me-1"></i>
                        Your membership is active and in good standing
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="card border-0 shadow-sm">
                    <div className="card-body text-center p-4">
                      <button
                        onClick={() => {
                          // Pre-select the vendor's registered tier when opening the modal
                          if (dashboardData.profile?.membership_type === 'vendor' && dashboardData.profile?.vendor_tier) {
                            const tier = dashboardData.profile.vendor_tier.toLowerCase();
                            const tierPrices = { bronze: 150, silver: 200, gold: 250, platinum: 300, diamond: 500 };
                            setSelectedMembership({ id: `vendor_${tier}`, name: `${dashboardData.profile.vendor_tier} Partner`, duration: '1 Year', price: tierPrices[tier] || 150 });
                          }
                          setShowPaymentModal(true);
                        }}
                        className="btn btn-lg w-100 fw-bold text-white mb-3"
                        style={{ backgroundColor: '#dc3545', border: 'none' }}
                      >
                        <i className="fas fa-exclamation-circle me-2"></i>
                        Membership Fee Due / Renewal Required - Click to Pay Now
                      </button>
                      <p className="text-muted small mb-0">
                        <i className="fas fa-info-circle me-1"></i>
                        Your membership is pending payment. Click the button above to complete your payment.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Main Content Grid */}
            <div className="row g-3 g-md-4">

              {/* ── Vendor Company Card (only for vendor members) ── */}
              {dashboardData.profile?.membership_type === 'vendor' && (
                <div className="col-12">
                  <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #6B8E23' }}>
                    <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                      <h3 className="h5 mb-0" style={{ color: '#556B2F' }}>
                        <FaBuilding className="me-2" style={{ color: '#6B8E23' }} />
                        Company / Business Information
                      </h3>
                      <span className="badge px-3 py-2 fw-bold" style={{
                        backgroundColor:
                          dashboardData.profile?.vendor_tier === 'Diamond' ? '#b9f2ff' :
                            dashboardData.profile?.vendor_tier === 'Platinum' ? '#e5e4e2' :
                              dashboardData.profile?.vendor_tier === 'Gold' ? '#FFD700' :
                                dashboardData.profile?.vendor_tier === 'Silver' ? '#C0C0C0' : '#CD7F32',
                        color: '#333'
                      }}>
                        <FaStar className="me-1" />
                        {dashboardData.profile?.vendor_tier || 'Bronze'} Partner
                      </span>
                    </div>
                    <div className="card-body">
                      <div className="row g-3">
                        {/* Logo + Company Name */}
                        <div className="col-12 col-md-4 d-flex align-items-center gap-3">
                          {dashboardData.profile?.vendor_logo ? (
                            <img
                              src={dashboardData.profile.vendor_logo.startsWith('http')
                                ? dashboardData.profile.vendor_logo
                                : `${process.env.REACT_APP_BACKEND_URL || 'https://api.africaresp.org'}${dashboardData.profile.vendor_logo}`}
                              alt="Company Logo"
                              style={{ width: '72px', height: '72px', objectFit: 'contain', border: '1px solid #dee2e6', borderRadius: '8px', padding: '4px', background: '#fff' }}
                            />
                          ) : (
                            <div className="d-flex align-items-center justify-content-center rounded" style={{ width: '72px', height: '72px', backgroundColor: '#f0f4e8', border: '1px solid #c8d8a8' }}>
                              <FaBuilding size={32} style={{ color: '#6B8E23' }} />
                            </div>
                          )}
                          <div>
                            <div className="fw-bold text-dark fs-6">{dashboardData.profile?.company_name || 'Your Company'}</div>
                            <div className="text-muted small">{dashboardData.profile?.vendor_category || 'Industry not set'}</div>
                            <span className={`badge mt-1 bg-${dashboardData.profile?.vendor_status === 'approved' ? 'success' :
                              dashboardData.profile?.vendor_status === 'rejected' ? 'danger' :
                                dashboardData.profile?.vendor_status === 'suspended' ? 'secondary' : 'warning'
                              }`}>
                              {dashboardData.profile?.vendor_status
                                ? dashboardData.profile.vendor_status.charAt(0).toUpperCase() + dashboardData.profile.vendor_status.slice(1)
                                : 'Pending Review'}
                            </span>
                          </div>
                        </div>

                        {/* Company Rep */}
                        <div className="col-12 col-md-4">
                          <div className="mb-2">
                            <small className="text-muted d-block">Company Representative</small>
                            <span className="fw-medium">{dashboardData.profile?.contact_person_name || 'Not set'}</span>
                          </div>
                          <div className="mb-2">
                            <small className="text-muted d-block">Rep Title / Role</small>
                            <span className="fw-medium">{dashboardData.profile?.contact_person_title || 'Not set'}</span>
                          </div>
                          <div>
                            <small className="text-muted d-block">Business Phone</small>
                            <span className="fw-medium">{dashboardData.profile?.vendor_phone || dashboardData.profile?.phone || 'Not set'}</span>
                          </div>
                        </div>

                        {/* Website + Address */}
                        <div className="col-12 col-md-4">
                          <div className="mb-2">
                            <small className="text-muted d-block">Website</small>
                            {dashboardData.profile?.vendor_website ? (
                              <a href={dashboardData.profile.vendor_website} target="_blank" rel="noopener noreferrer" className="text-primary small">
                                <FaGlobe className="me-1" />
                                {dashboardData.profile.vendor_website}
                              </a>
                            ) : (
                              <span className="text-muted small">Not set</span>
                            )}
                          </div>
                          <div className="mb-2">
                            <small className="text-muted d-block">Company Size</small>
                            <span className="fw-medium">{dashboardData.profile?.company_size || 'Not set'}</span>
                          </div>
                          <div>
                            <small className="text-muted d-block">Country</small>
                            <span className="fw-medium">{dashboardData.profile?.vendor_country || dashboardData.profile?.country || 'Not set'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="mt-3 pt-3 border-top d-flex flex-wrap gap-2">
                        <Link to="/profile" className="btn btn-sm fw-semibold" style={{ backgroundColor: '#6B8E23', color: 'white', border: 'none' }}>
                          <FaBuilding className="me-1" />
                          View Full Company Profile
                        </Link>
                        {/* Certificate only available after admin approves the vendor */}
                        {dashboardData.profile?.vendor_status === 'approved' && (
                          <button
                            onClick={() => setShowCertificateModal(true)}
                            className="btn btn-sm fw-semibold text-white"
                            style={{ backgroundColor: '#556B2F', border: 'none' }}
                          >
                            <FaCertificate className="me-1" />
                            Download Vendor Certificate
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Profile Summary */}
              <div className="col-12 col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white border-bottom">
                    <h3 className="h5 mb-0">Profile Summary</h3>
                  </div>
                  <div className="card-body">
                    <div className="d-flex align-items-center mb-3">
                      {dashboardData.profile?.profile_picture_url ? (
                        <img
                          src={dashboardData.profile.profile_picture_url.startsWith('http')
                            ? dashboardData.profile.profile_picture_url
                            : `${process.env.REACT_APP_BACKEND_URL || 'https://api.africaresp.org'}${dashboardData.profile.profile_picture_url}`}
                          alt="Profile"
                          className="rounded-circle me-3"
                          style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                        />
                      ) : (
                        <div className="bg-secondary rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '60px', height: '60px' }}>
                          <FaUser className="text-white" size={30} />
                        </div>
                      )}
                      <div>
                        <h4 className="h6 mb-1">
                          {dashboardData.profile?.title && `${dashboardData.profile.title}. `}
                          {dashboardData.profile?.full_name || 'Complete Your Profile'}
                        </h4>
                        {dashboardData.profile?.membership_id && (
                          <p className="text-muted small mb-1">
                            <strong className="text-primary">ID:</strong> {dashboardData.profile.membership_id}
                          </p>
                        )}
                        <p className="text-muted small mb-0">{dashboardData.profile?.email}</p>
                      </div>
                    </div>

                    <div className="row g-2 mb-3">
                      <div className="col-6">
                        <small className="text-muted">Membership Start</small>
                        <p className="mb-0">
                          {dashboardData.profile?.membership_start_date
                            ? new Date(dashboardData.profile.membership_start_date).toLocaleDateString()
                            : 'N/A'}
                        </p>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">Membership End</small>
                        <p className="mb-0">
                          {dashboardData.profile?.membership_end_date
                            ? new Date(dashboardData.profile.membership_end_date).toLocaleDateString()
                            : 'N/A'}
                        </p>
                      </div>
                    </div>

                    <div className="row g-2 mb-3">
                      <div className="col-6">
                        <small className="text-muted">Membership Type</small>
                        <p className="mb-0 text-capitalize">
                          {dashboardData.profile?.membership_type || 'N/A'}
                        </p>
                      </div>
                      <div className="col-6">
                        <small className="text-muted">Membership Status</small>
                        <p className="mb-0">
                          <span className={`badge bg-${dashboardData.profile?.membership_status === 'active' ? 'success' : 'warning'}`}>
                            {dashboardData.profile?.membership_status || 'N/A'}
                          </span>
                        </p>
                      </div>
                    </div>

                    <Link to="/profile" className="btn btn-outline-primary btn-sm w-100 mb-2">
                      View Full Profile
                    </Link>

                    {/* Certificate Download Button - active members OR approved vendors only */}
                    {(dashboardData.profile?.membership_status === 'active' || dashboardData.profile?.vendor_status === 'approved') && (
                      <button
                        onClick={() => setShowCertificateModal(true)}
                        className="btn btn-sm w-100 fw-bold text-white mb-2"
                        style={{ backgroundColor: '#6B8E23', border: 'none' }}
                      >
                        <i className="fas fa-certificate me-2"></i>
                        {dashboardData.profile?.membership_type === 'vendor' ? 'Download Vendor Certificate' : 'Download Membership Certificate'}
                      </button>
                    )}

                    {/* Receipt Download Button - Only visible if member has receipts */}
                    {userReceipts.length > 0 && (
                      <button
                        onClick={() => downloadReceipt(userReceipts[0].receipt_number)}
                        className="btn btn-sm w-100 fw-bold text-white"
                        style={{ backgroundColor: '#0d6efd', border: 'none' }}
                      >
                        <i className="fas fa-receipt me-2"></i>
                        Download Payment Receipt
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Registered Events */}
              <div className="col-12 col-lg-8">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                    <h3 className="h5 mb-0">Your Registered Events</h3>
                    <Link to="/events" className="btn btn-sm btn-outline-primary">View All Events</Link>
                  </div>
                  <div className="card-body">
                    {dashboardData.registeredEvents.length > 0 ? (
                      <div className="d-grid gap-3">
                        {dashboardData.registeredEvents.map((event) => (
                          <div key={event.event_id} className="border rounded p-3">
                            <div className="d-flex justify-content-between">
                              <h4 className="h6 mb-1">{event.event_name}</h4>
                              <span className={`badge ${event.category === 'Paid' ? 'bg-success' : 'bg-primary'}`}>
                                {event.category}
                              </span>
                            </div>

                            <div className="d-flex align-items-center text-muted small mb-1">
                              <FaCalendarAlt className="me-1" size={12} />
                              <span>{formatDate(event.start_date)}</span>
                            </div>

                            <div className="d-flex align-items-center text-muted small mb-1">
                              <FaClock className="me-1" size={12} />
                              <span>{formatTime(event.start_date)} ({event.duration} min)</span>
                            </div>

                            <div className="d-flex align-items-center text-muted small mb-2">
                              {event.medium === 'Online' ? (
                                <FaVideo className="me-1" size={12} />
                              ) : (
                                <FaMapMarkerAlt className="me-1" size={12} />
                              )}
                              <span>{event.medium}</span>
                            </div>

                            <div className="d-flex justify-content-between align-items-center">
                              <span className={`badge bg-${event.attendance_status === 'Registered' ? 'info' : 'success'}`}>
                                {event.attendance_status}
                              </span>
                              <Link to={`/events/${event.event_id}`} className="btn btn-sm btn-outline-primary">
                                View Details
                              </Link>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-muted">You haven't registered for any events yet.</p>
                        <Link to="/events" className="btn btn-primary">Browse Events</Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Enrolled Courses */}
              <div className="col-12 col-lg-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                    <h3 className="h5 mb-0">Enrolled Courses</h3>
                    <Link to="/courses" className="btn btn-sm btn-outline-primary">View All</Link>
                  </div>
                  <div className="card-body">
                    {dashboardData.enrolledCourses.length > 0 ? (
                      <div className="d-grid gap-3">
                        {dashboardData.enrolledCourses.map((course) => (
                          <div key={course.id} className="border rounded p-3">
                            <h4 className="h6 mb-2">{course.title || course.course_title}</h4>
                            <div className="mb-2">
                              <div className="d-flex justify-content-between small mb-1">
                                <span>Progress</span>
                                <span>{course.progress_percentage || 0}%</span>
                              </div>
                              <div className="progress" style={{ height: '6px' }}>
                                <div
                                  className="progress-bar"
                                  role="progressbar"
                                  style={{ width: `${course.progress_percentage || 0}%` }}
                                ></div>
                              </div>
                            </div>
                            <div className="d-flex justify-content-between small text-muted">
                              <span>
                                CPD Points: {course.cpd_points || 0}
                              </span>
                              <span>{course.status || 'Enrolled'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-muted">You haven't enrolled in any courses yet.</p>
                        <Link to="/courses" className="btn btn-primary">Browse Courses</Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="col-12 col-lg-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-white border-bottom">
                    <h3 className="h5 mb-0">Quick Actions</h3>
                  </div>
                  <div className="card-body">
                    <div className="row g-2">
                      <div className="col-6">
                        <Link to="/events" className="text-decoration-none">
                          <div className="card border text-center p-3 h-100">
                            <FaCalendarAlt className="text-primary mx-auto mb-2" size={24} />
                            <p className="small fw-medium mb-0 text-dark">Events</p>
                          </div>
                        </Link>
                      </div>
                      <div className="col-6">
                        <Link to="/courses" className="text-decoration-none">
                          <div className="card border text-center p-3 h-100">
                            <FaUser className="text-success mx-auto mb-2" size={24} />
                            <p className="small fw-medium mb-0 text-dark">Courses</p>
                          </div>
                        </Link>
                      </div>
                      <div className="col-6">
                        <Link to="/messages" className="text-decoration-none">
                          <div className="card border text-center p-3 h-100" style={{ borderColor: '#6B8E23' }}>
                            <i className="bi bi-envelope-fill mx-auto mb-2" style={{ color: '#6B8E23', fontSize: '24px' }}></i>
                            <p className="small fw-medium mb-0 text-dark">Messages</p>
                          </div>
                        </Link>
                      </div>
                      <div className="col-6">
                        <Link to="/store" className="text-decoration-none">
                          <div className="card border text-center p-3 h-100">
                            <FaUser className="text-info mx-auto mb-2" size={24} />
                            <p className="small fw-medium mb-0 text-dark">Store</p>
                          </div>
                        </Link>
                      </div>
                      <div className="col-6">
                        <Link to="/community" className="text-decoration-none">
                          <div className="card border text-center p-3 h-100">
                            <FaUser className="text-warning mx-auto mb-2" size={24} />
                            <p className="small fw-medium mb-0 text-dark">Community</p>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* End Main Content */}

      {/* Membership Payment Modal */}
      {showPaymentModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowPaymentModal(false)}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                <h5 className="modal-title fw-bold">
                  <i className="fas fa-credit-card me-2"></i>
                  {dashboardData.profile?.membership_type === 'vendor' ? 'Select Vendor Tier & Complete Payment' : 'Select Membership Type & Complete Payment'}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowPaymentModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="alert alert-info mb-4">
                  <i className="fas fa-info-circle me-2"></i>
                  Please select your membership type below and proceed to payment. After payment, an admin will activate your membership.
                </div>

                {/* Membership Type Selection */}
                <div className="row g-3">
                  {(dashboardData.profile?.membership_type === 'vendor'
                    ? [
                      { id: 'vendor_bronze', name: 'Bronze Partner', duration: '1 Year', price: 150, description: 'Entry-level industry partnership', benefits: ['Certificate of partnership recognition', 'Access to ARCA newsletters and communication channels', 'ARCA Bronze Industry Partner Badge'] },
                      { id: 'vendor_silver', name: 'Silver Partner', duration: '1 Year', price: 200, description: 'Digital presence & community access', benefits: ['Digital advertisement on ARCA Website (12 months)', 'Quarterly social media acknowledgment', 'ARCA Silver Industry Partner Badge'] },
                      { id: 'vendor_gold', name: 'Gold Partner', duration: '1 Year', price: 250, description: 'Enhanced visibility & networking', benefits: ['Advertise on ARCA Website', 'Quarterly social media acknowledgment', 'ARCA Gold Industry Partner Badge'] },
                      { id: 'vendor_platinum', name: 'Platinum Partner', duration: '1 Year', price: 300, description: 'Strategic collaboration & promotion', benefits: ['Digital advertisement on ARCA Website (12 months)', 'Direct access to ARCA leadership', 'Featured placement in all ARCA newsletters'] },
                      { id: 'vendor_diamond', name: 'Diamond Partner', duration: '1 Year', price: 500, description: 'Top-tier exclusive partnership', benefits: ['Top logo placement on ARCA website vendor directory', 'Exclusive Event Access & Speaking Opportunities', 'Enhanced Recognition, Diamond Badge'] }
                    ]
                    : membershipTypes
                  ).map((membership) => (
                    <div key={membership.id} className="col-12">
                      <div
                        className={`card h-100 cursor-pointer ${selectedMembership?.id === membership.id ? 'border-success border-3' : 'border'
                          }`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedMembership(membership)}
                      >
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center mb-2">
                                <input
                                  type="radio"
                                  name="membership"
                                  checked={selectedMembership?.id === membership.id}
                                  onChange={() => setSelectedMembership(membership)}
                                  className="form-check-input me-3"
                                  style={{ cursor: 'pointer' }}
                                />
                                <div>
                                  <h6 className="mb-0 fw-bold">{membership.name}</h6>
                                  <small className="text-muted">{membership.duration}</small>
                                </div>
                              </div>
                              <p className="text-muted small mb-2">{membership.description}</p>
                              <ul className="small mb-0">
                                {membership.benefits.slice(0, 3).map((benefit, idx) => (
                                  <li key={idx}>{benefit}</li>
                                ))}
                              </ul>
                            </div>
                            <div className="text-end ms-3">
                              <h4 className="mb-0 fw-bold" style={{ color: '#6B8E23' }}>
                                ${membership.price}
                              </h4>
                              <small className="text-muted">USD</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowPaymentModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-lg text-white fw-bold"
                  style={{ backgroundColor: '#6B8E23', border: 'none' }}
                  disabled={!selectedMembership}
                  onClick={() => {
                    if (selectedMembership) {
                      // Redirect to payment page with selected membership
                      navigate('/payments', {
                        state: {
                          membershipType: selectedMembership.id,
                          membershipName: selectedMembership.name,
                          duration: selectedMembership.duration,
                          amount: selectedMembership.price
                        }
                      });
                    }
                  }}
                >
                  <i className="fas fa-arrow-right me-2"></i>
                  Proceed to Payment ${selectedMembership ? selectedMembership.price : '0'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Membership Certificate Modal */}
      {showCertificateModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowCertificateModal(false)}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                <h5 className="modal-title fw-bold">
                  <i className="fas fa-certificate me-2"></i>
                  ARCA Membership Certificate
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowCertificateModal(false)}></button>
              </div>
              <div className="modal-body p-4" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <MembershipCertificate
                  membershipStatus={dashboardData.profile?.membership_status}
                  membershipType={dashboardData.profile?.membership_type}
                  onClose={() => setShowCertificateModal(false)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </PublicLayout>
  );
};

export default MemberDashboard;